import React, {useEffect, useState} from 'react';
import clinicService from '../../../../../services/clinic.service';
import {useParams} from 'react-router-dom';
import {useForm} from 'react-hook-form';
import ClinicServicesForm from './ClinicServicesForm';
import Loading from "../../../../common/Loading";

const ClinicServicesCard = () => {
    const {register, handleSubmit, formState: {errors}} = useForm();
    const [clinicSettings, setClinicSettings] = useState({});
    const [isLoading, setIsLoading] = useState(false);
    const [isError, setIsError] = useState(false);
    const [errorMessage, setErrorMessage] = useState('');
    const {clinicId} = useParams();
    const [isEditing, setIsEditing] = useState(false);
    const [copied, setCopied] = useState(false);
    const [lastModified, setLastModified] = useState(null);

    useEffect(() => {
        setIsLoading(true);
        clinicService.getSettings(clinicId)
            .then(res => {
                setIsLoading(false);
                setIsError(false);
                setErrorMessage('');
                setClinicSettings(res.data);
                setLastModified(res.data.modifiedAt);
            })
            .catch(err => {
                setIsLoading(false);
                setIsError(true);
                setErrorMessage(err.response.data.message);
                console.log(err);
            });
    }, [clinicId]);

    const updateSettings = (settings) => {
        setClinicSettings(settings);
        setIsEditing(false);
    }

    const copyToClipboard = () => {
        navigator.clipboard.writeText(clinicId);
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
    };

    const getServiceStatus = (enabled) => {
        return enabled ? (
            <span className="badge bg-success bg-opacity-10 text-success ms-2">
                <i className="bi bi-check-circle-fill me-1"></i> ENABLED
            </span>
        ) : (
            <span className="badge bg-danger bg-opacity-10 text-danger ms-2">
                <i className="bi bi-x-circle-fill me-1"></i> DISABLED
            </span>
        );
    };

    const getServiceIcon = (serviceName) => {
        switch (serviceName) {
            case "reminder":
            case "reminderEmail":
            case "reminderCall":
            case "reminderSms":
                return <i className="bi bi-bell me-2"></i>;
            case "recall":
                return <i className="bi bi-calendar-check me-2"></i>;
            case "reputation":
                return <i className="bi bi-star me-2"></i>;
            case "survey":
            case "surveyReminder":
                return <i className="bi bi-clipboard-check me-2"></i>;
            case "booking":
                return <i className="bi bi-bookmark-check me-2"></i>;
            case "twoWayText":
                return <i className="bi bi-chat-left-text me-2"></i>;
            case "isAntispamLaw":
                return <i className="bi bi-shield-lock me-2"></i>;
            case "newsletterEmail":
            case "newsletterSms":
                return <i className="bi bi-envelope me-2"></i>;
            case "blastEmail":
            case "blastSms":
            case "blastCall":
                return <i className="bi bi-megaphone me-2"></i>;
            default:
                return <i className="bi bi-gear me-2"></i>;
        }
    };

    const getServiceLabel = (serviceKey) => {
        const labels = {
            reminder: "Appointment Reminders",
            recall: "Patient Recalls",
            reputation: "Reputation Management",
            survey: "Patient Surveys",
            surveyReminder: "Survey Reminders",
            booking: "Online Booking",
            twoWayText: "Two-way Text Messaging",
            isAntispamLaw: "Anti-Spam Compliance",
            reminderEmail: "Email Reminders",
            reminderCall: "Phone Reminders",
            reminderSms: "SMS Reminders",
            newsletterEmail: "Email Newsletter",
            newsletterSms: "SMS Newsletter",
            blastEmail: "Email Blasts",
            blastSms: "SMS Blasts",
            blastCall: "Call Campaigns"
        };
        return labels[serviceKey] || serviceKey;
    };

    return (
        <div className="col-lg-8">
            <div className="card-padding shadow-none">
                <div className="d-flex justify-content-between align-items-center">
                    <div className="card-title mb-0">
                        <i className="bi bi-gear me-2"></i> Clinic Services
                    </div>
                    <div className="d-flex align-items-center">
                            <span className="badge bg-light text-dark me-2">
                                Clinic ID: {clinicId}
                            </span>
                        <button
                            onClick={copyToClipboard}
                            className="btn btn-sm btn-outline-primary"
                            title="Copy to clipboard"
                        >
                            {copied ? (
                                <i className="bi bi-check2"></i>
                            ) : (
                                <i className="bi bi-copy"></i>
                            )}
                        </button>
                        {isEditing ? (
                            <button
                                className="btn btn-outline-danger btn-sm ms-2"
                                onClick={() => setIsEditing(false)}
                            >
                                <i className="bi bi-x-lg me-1"></i> Cancel
                            </button>
                        ) : (
                            <button
                                className="btn btn-outline-primary btn-sm ms-2"
                                onClick={() => setIsEditing(true)}
                            >
                                <i className="bi bi-pencil me-1"></i> Edit
                            </button>
                        )}
                    </div>
                </div>

                {isLoading ? (
                    <Loading/>
                ) : isEditing ? (
                    <ClinicServicesForm clinicSettings={clinicSettings} updateSettings={updateSettings}/>
                ) : (
                    <>
                        {/* Summary Stats */}
                        <div className="d-flex flex-wrap justify-content-between gap-3 mb-4">
                            <div className="d-flex align-items-start">
                                <span className="badge bg-success rounded-circle p-2 me-2"></span>
                                <span className="text-success fw-normal">
                                        {Object.values(clinicSettings).filter(val => val === true).length} ENABLED
                                    </span>
                            </div>
                            <div className="d-flex flex-wrap align-items-end">
                                {lastModified && (
                                    <small className="text-muted mb-1">
                                        Last Modified: {lastModified}
                                    </small>
                                )}
                            </div>
                        </div>

                        {/* Services List */}
                        <div className="list-group">
                            {Object.entries(clinicSettings).map(([key, value]) => {
                                // Skip non-service fields
                                if (['clinicId', 'id', 'createdAt', 'modifiedAt'].includes(key)) {
                                    return null;
                                }

                                return (
                                    <div
                                        key={key}
                                        className="list-group-item list-group-item-action d-flex justify-content-between align-items-center"
                                    >
                                        <div className="d-flex align-items-center">
                                            {getServiceIcon(key)}
                                            <span>{getServiceLabel(key)}</span>
                                        </div>
                                        <div>
                                            {getServiceStatus(value === true)}
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    </>
                )}

                {isError && (
                    <div className="alert alert-danger mt-3">
                        <i className="bi bi-exclamation-triangle-fill me-2"></i>
                        {errorMessage}
                    </div>
                )}
            </div>
        </div>
    );
};

export default ClinicServicesCard;